import { useRef, useEffect, useMemo } from 'react';
import { useVeloraStore, PitchPoint } from '../store/useVeloraStore';
import { frequencyToNote } from '../lib/noteMap';

const CANVAS_H = 220;
const VISIBLE_FRAMES = 100;
const LABEL_W = 36;

const NOTE_NAMES_CHROMATIC = ['C','C#','D','D#','E','F','F#','G','G#','A','A#','B'];

/** Build display notes from user's measured range + padding.
 *  Falls back to a wide default (E2–C6) when no range measured yet. */
function buildDisplayNotes(lowestHz: number | null, highestHz: number | null): string[] {
  const PAD = 3; // semitones padding above and below

  let loMidi: number;
  let hiMidi: number;

  if (lowestHz && highestHz && lowestHz > 0 && highestHz > 0) {
    const lo = frequencyToNote(lowestHz);
    const hi = frequencyToNote(highestHz);
    loMidi = lo.midi - PAD;
    hiMidi = hi.midi + PAD;
  } else {
    // default: E2 (midi 40) → C6 (midi 84) — covers all voice types
    loMidi = 40;
    hiMidi = 84;
  }

  const notes: string[] = [];
  for (let midi = hiMidi; midi >= loMidi; midi--) {
    const octave = Math.floor(midi / 12) - 1;
    const name = NOTE_NAMES_CHROMATIC[midi % 12];
    notes.push(`${name}${octave}`);
  }
  return notes;
}

function centsColor(cents: number): string {
  const a = Math.abs(cents);
  if (a <= 5)  return '#4ade80';
  if (a <= 12) return '#facc15';
  return '#f87171';
}

export default function PitchVisualizer() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { pitchHistory, currentNote, currentCents, referenceNotes, lowestHz, highestHz } = useVeloraStore();

  const DISPLAY_NOTES = useMemo(
    () => buildDisplayNotes(lowestHz, highestHz),
    [lowestHz, highestHz]
  );

  const NOTE_ROW_H = Math.max(10, Math.min(18, Math.floor(CANVAS_H / DISPLAY_NOTES.length)));

  function noteToRow(fullName: string): number {
    return DISPLAY_NOTES.indexOf(fullName);
  }

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const W = canvas.width;
    const H = CANVAS_H;
    const graphW = W - LABEL_W;

    ctx.clearRect(0, 0, W, H);

    // Background
    ctx.fillStyle = '#0a0820';
    ctx.fillRect(0, 0, W, H);

    // Note rows
    DISPLAY_NOTES.forEach((name, i) => {
      const y = i * NOTE_ROW_H;
      const isBlack = name.includes('#');
      ctx.fillStyle = isBlack ? 'rgba(22,18,58,0.95)' : 'rgba(30,26,70,0.95)';
      ctx.fillRect(LABEL_W, y, graphW, NOTE_ROW_H - 1);

      ctx.fillStyle = '#AFA9EC';
      ctx.font = '9px monospace';
      ctx.textAlign = 'right';
      ctx.fillText(name, LABEL_W - 4, y + 13);

      ctx.strokeStyle = 'rgba(83,74,183,0.2)';
      ctx.lineWidth = 0.5;
      ctx.beginPath();
      ctx.moveTo(LABEL_W, y + NOTE_ROW_H - 1);
      ctx.lineTo(W, y + NOTE_ROW_H - 1);
      ctx.stroke();
    });

    const slice = pitchHistory.slice(-VISIBLE_FRAMES);
    const xStep = graphW / VISIBLE_FRAMES;

    // ── Target notes line (purple) ──────────────────────────────────────────
    if (referenceNotes.length > 0) {
      ctx.strokeStyle = '#7F77DD';
      ctx.lineWidth = 2;
      ctx.setLineDash([4, 3]);
      ctx.beginPath();
      let started = false;
      referenceNotes.slice(-VISIBLE_FRAMES).forEach((ref, i) => {
        const row = noteToRow(ref.note);
        if (row === -1) { started = false; return; }
        const x = LABEL_W + i * xStep;
        const y = row * NOTE_ROW_H + NOTE_ROW_H / 2;
        started ? ctx.lineTo(x, y) : ctx.moveTo(x, y);
        started = true;
      });
      ctx.stroke();
      ctx.setLineDash([]);
    }

    // ── Voice line (colored by accuracy) ────────────────────────────────────
    slice.forEach((p: PitchPoint, i: number) => {
      if (!p.note || p.note === '--') return;
      const row = noteToRow(p.note);
      if (row === -1) return;

      const x = LABEL_W + i * xStep;
      const y = row * NOTE_ROW_H + NOTE_ROW_H / 2;
      const color = centsColor(p.cents);

      // Connect to previous point
      if (i > 0) {
        const prev = slice[i - 1];
        if (prev.note && prev.note !== '--') {
          const prevRow = noteToRow(prev.note);
          if (prevRow !== -1) {
            ctx.strokeStyle = color;
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.moveTo(LABEL_W + (i - 1) * xStep, prevRow * NOTE_ROW_H + NOTE_ROW_H / 2);
            ctx.lineTo(x, y);
            ctx.stroke();
          }
        }
      }

      // Dot
      ctx.beginPath();
      ctx.arc(x, y, 2.5, 0, Math.PI * 2);
      ctx.fillStyle = color;
      ctx.globalAlpha = 0.7 + p.stability * 0.3;
      ctx.fill();
      ctx.globalAlpha = 1;

      // Cents deviation label every 5 frames
      if (i % 5 === 0 && Math.abs(p.cents) > 3) {
        ctx.fillStyle = color;
        ctx.font = '8px monospace';
        ctx.textAlign = 'center';
        const label = `${p.cents > 0 ? '+' : ''}${p.cents}`;
        ctx.fillText(label, x, y - 5);
      }
    });

    // Current note highlight
    const curRow = noteToRow(currentNote);
    if (curRow !== -1) {
      const color = centsColor(currentCents);
      ctx.fillStyle = `${color}18`;
      ctx.fillRect(LABEL_W, curRow * NOTE_ROW_H, graphW, NOTE_ROW_H - 1);
      ctx.fillStyle = color;
      ctx.fillRect(W - 4, curRow * NOTE_ROW_H + 3, 4, NOTE_ROW_H - 7);
    }
  }, [pitchHistory, currentNote, currentCents, referenceNotes]);

  return (
    <canvas ref={canvasRef} width={window.innerWidth - 32} height={CANVAS_H}
      className="w-full rounded-xl" style={{ background: '#0a0820' }} />
  );
}
